"use client";

import React from "react";
import { ArrowDown, ArrowUp } from "lucide-react";
import { SortOrderEnum } from "modules/collections/details/model/collection";
import { cn } from "utils/utils";
import {
  RadioGroup,
  RadioOption,
} from "modules/common/components/radioGroup/RadioGroup";
import { ICON_SIZE } from "modules/common/components/navigation/Menu";

export interface SortOption<T> {
  key: string;
  name: string;
  enum: T;
}

interface SortOptionsRadioGroupProps<T> {
  options: SortOption<T>[];
  currentSort: T;
  onSortChange: (sort: T) => void;
  disabled?: boolean;
  className?: string;
  label?: string;
}

export function SortOptionsRadioGroup<T>({
  options,
  currentSort,
  onSortChange,
  disabled = false,
  className,
  label = "Sort By",
}: SortOptionsRadioGroupProps<T>) {
  const radioOptions: RadioOption<T>[] = options.map((option) => ({
    value: option.enum,
    label: option.name,
    key: option.key,
  }));

  return (
    <div className={cn("form-control", className)}>
      {label && (
        <label className="label font-medium">
          <span className="label-text text-base-content">{label}</span>
        </label>
      )}
      <RadioGroup
        options={radioOptions}
        value={currentSort}
        onChange={onSortChange}
        name="sort-options"
        orientation="vertical"
        disabled={disabled}
      />
    </div>
  );
}

interface SortDirectionRadioGroupProps {
  currentOrder: SortOrderEnum;
  onOrderChange: (order: SortOrderEnum) => void;
  disabled?: boolean;
  className?: string;
  label?: string;
}

export function SortDirectionRadioGroup({
  currentOrder,
  onOrderChange,
  disabled = false,
  className,
  label = "Sort Direction",
}: SortDirectionRadioGroupProps) {
  const sortDirectionOptions: RadioOption<SortOrderEnum>[] = [
    {
      value: SortOrderEnum.Ascending,
      label: "Ascending",
      icon: <ArrowUp size={ICON_SIZE} />,
    },
    {
      value: SortOrderEnum.Descending,
      label: "Descending",
      icon: <ArrowDown size={ICON_SIZE} />,
    },
  ];

  return (
    <div className={cn("form-control", className)}>
      {label && (
        <label className="label font-medium">
          <span className="label-text text-base-content">{label}</span>
        </label>
      )}
      <RadioGroup
        options={sortDirectionOptions}
        value={currentOrder}
        onChange={onOrderChange}
        name="sort-direction"
        orientation="horizontal"
        disabled={disabled}
        optionClassName="btn-sm flex-1"
      />
    </div>
  );
}

interface ViewModeRadioGroupProps<T> {
  views: Array<T>;
  activeView: number; // Now expects index (0, 1, etc.)
  onViewChange: (index: number) => void; // Now passes index
  nameSelector?: (view: T) => string;
  iconSelector?: (view: T) => React.ReactNode;
  disabled?: boolean;
  className?: string;
  label?: string;
  orientation?: "vertical" | "horizontal";
}

export function ViewModeRadioGroup<T>({
  views,
  activeView,
  onViewChange,
  nameSelector = (view: any) => view.name || String(view),
  iconSelector,
  disabled = false,
  className,
  label = "View Mode",
  orientation = "vertical",
}: ViewModeRadioGroupProps<T>) {
  // Map views to radio options using indices as values
  const radioOptions: RadioOption<number>[] = views.map((view, index) => ({
    value: index, // Use index as value for URL state
    label: nameSelector(view),
    key: `view-${index}`,
    icon: iconSelector ? iconSelector(view) : undefined,
  }));

  return (
    <div className={cn("form-control gap-2", className)}>
      {label && (
        <span className="text-base-content text-base font-medium">{label}</span>
      )}
      <RadioGroup
        options={radioOptions}
        value={activeView} // activeView is now a number (index)
        onChange={onViewChange} // Calls with index directly
        name="view-mode"
        orientation={orientation}
        disabled={disabled}
        optionClassName={cn("btn-sm", orientation === "horizontal" && "flex-1")}
      />
    </div>
  );
}
